/**
 * Record activity — the record's history, in one timeline.
 *
 * Every record in the portal is touched by something before a human sees it: a
 * call is triaged, a form entry is classified, someone overrides a verdict. All
 * of that used to be rendered on the record's Overview, next to the facts,
 * which is how a form entry ended up leading with a paragraph explaining its
 * own classification instead of saying who submitted it.
 *
 * The rule this component encodes: the Overview states WHAT the record is; this
 * tab answers WHY the system thinks so, and WHAT people have said since. A
 * reader who never asks the question never has to read the answer, and a reader
 * who does has one place to look — including which Switch produced the verdict,
 * so a wrong classification points at the thing that needs configuring.
 *
 * Notes are part of that history rather than a separate list beneath it. A note
 * and an analysis verdict answer the same question — "what has happened to this
 * record" — so interleaving them by time is the only ordering that reads
 * correctly. Pass `onAddNote` to enable the composer; omit it and this renders
 * exactly as it always did.
 */

import { useState, type ReactNode } from 'react';
import { Activity, Mail, MessageSquare, Sparkles, UserCog } from 'lucide-react';
import type { SharedRecordActivityEntry, SharedRecordNote } from '@oneup/shared';
import { SectionHeading, formatDateTimeLabel } from './record-presentation';

export interface RecordAnalysisEntry {
  id: string;
  /** The analysis or Switch that ran, e.g. "Intelligent Form Triage". */
  source: string;
  /** What it concluded, e.g. "Likely Service Request". */
  result?: string;
  /** Why it concluded that, in the analysis's own words. */
  explanation?: string;
  occurredAt?: string;
  /** Present when a person changed the verdict rather than the analysis setting it. */
  actor?: string;
}

/**
 * Short enough that a note stays a note. Long-form context belongs in a message
 * thread, where the other side can reply; a note nobody is notified about is
 * the wrong place to write a paragraph.
 */
export const RECORD_NOTE_MAX_LENGTH = 300;

type TimelineItem =
  | { kind: 'analysis'; at: string; entry: RecordAnalysisEntry }
  | { kind: 'event'; at: string; event: SharedRecordActivityEntry }
  | { kind: 'note'; at: string; note: SharedRecordNote };

export function RecordActivity({
  entries,
  events,
  children,
  emptyLabel = 'No analysis has run on this record yet.',
  notes,
  onAddNote,
  noteBusy = false,
  notePlaceholder = 'Add a note for this record…',
}: {
  entries: RecordAnalysisEntry[];
  /** Client-visible operational events returned by the record-detail API. */
  events?: SharedRecordActivityEntry[];
  /** Archive controls and anything else that belongs beneath the timeline. */
  children?: ReactNode;
  emptyLabel?: string;
  /** Notes to interleave into the timeline. */
  notes?: SharedRecordNote[];
  /** Supply to render the composer. Omit for a read-only timeline. */
  onAddNote?: (body: string) => Promise<void>;
  noteBusy?: boolean;
  notePlaceholder?: string;
}) {
  const timeline: TimelineItem[] = [
    ...entries.map((entry) => ({
      kind: 'analysis' as const,
      at: entry.occurredAt ?? '',
      entry,
    })),
    ...(events ?? []).map((event) => ({
      kind: 'event' as const,
      at: event.occurredAt,
      event,
    })),
    ...(notes ?? []).map((note) => ({ kind: 'note' as const, at: note.createdAt, note })),
  ]
    // Newest first, so the note just written is the first thing visible.
    // Undated analysis entries sort last rather than jumping to the top.
    .sort((a, b) => (b.at || '').localeCompare(a.at || ''));

  return (
    <div className="space-y-5">
      {onAddNote && (
        <NoteComposer onAddNote={onAddNote} busy={noteBusy} placeholder={notePlaceholder} />
      )}

      <section>
        <SectionHeading>{onAddNote || notes?.length || events?.length ? 'Timeline' : 'Analysis history'}</SectionHeading>
        {timeline.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <ol className="relative ml-1 space-y-4 border-l border-border pl-4">
            {timeline.map((item) =>
              item.kind === 'note' ? (
                <TimelineRow
                  key={`note-${item.note.id}`}
                  icon={<MessageSquare className="h-3 w-3" />}
                  title="Note"
                  meta={[item.note.authorName, formatDateTimeLabel(item.note.createdAt)]}
                >
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                    {item.note.body}
                  </p>
                </TimelineRow>
              ) : item.kind === 'event' ? (
                <TimelineRow
                  key={`event-${item.event.id}`}
                  icon={item.event.kind === 'automation'
                    ? <Mail className="h-3 w-3" />
                    : <Activity className="h-3 w-3" />}
                  title={item.event.title}
                  badge={item.event.status}
                  meta={[item.event.actorName, formatDateTimeLabel(item.event.occurredAt)]}
                >
                  {item.event.detail && (
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {item.event.detail}
                    </p>
                  )}
                </TimelineRow>
              ) : (
                <TimelineRow
                  key={`analysis-${item.entry.id}`}
                  icon={item.entry.actor ? <UserCog className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
                  title={item.entry.source}
                  badge={item.entry.result}
                  meta={[item.entry.actor, formatDateTimeLabel(item.entry.occurredAt)]}
                >
                  {item.entry.explanation && (
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {item.entry.explanation}
                    </p>
                  )}
                </TimelineRow>
              ),
            )}
          </ol>
        )}
      </section>
      {children}
    </div>
  );
}

function TimelineRow({
  icon,
  title,
  badge,
  meta,
  children,
}: {
  icon: ReactNode;
  title: string;
  badge?: string;
  meta: Array<string | undefined>;
  children?: ReactNode;
}) {
  const footer = meta.filter(Boolean).join(' · ');
  return (
    <li className="relative">
      <span
        className="absolute -left-[22px] top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-card text-primary"
        aria-hidden
      >
        {icon}
      </span>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-sm font-medium text-foreground">{title}</span>
        {badge && (
          <span className="rounded-full border border-border px-2 py-0.5 text-[11px] font-medium leading-4">
            {badge}
          </span>
        )}
      </div>
      {children}
      {footer && <p className="mt-1 text-xs text-muted-foreground">{footer}</p>}
    </li>
  );
}

/**
 * Composer, above the timeline.
 *
 * The counter only appears once the note is close to the cap — a character
 * count on an empty box reads as a restriction before anyone has tried to write
 * anything.
 */
function NoteComposer({
  onAddNote,
  busy,
  placeholder,
}: {
  onAddNote: (body: string) => Promise<void>;
  busy: boolean;
  placeholder: string;
}) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const disabled = busy || saving;
  const remaining = RECORD_NOTE_MAX_LENGTH - note.length;

  const save = async () => {
    const body = note.trim();
    if (!body) return;
    setSaving(true);
    setError(undefined);
    try {
      await onAddNote(body);
      setNote('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'This note could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <textarea
        value={note}
        onChange={(event) => setNote(event.target.value.slice(0, RECORD_NOTE_MAX_LENGTH))}
        placeholder={placeholder}
        rows={3}
        maxLength={RECORD_NOTE_MAX_LENGTH}
        disabled={disabled}
        aria-label="Add a note"
        className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition-shadow focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
      />
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          disabled={disabled || !note.trim()}
          onClick={() => void save()}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-opacity disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Add note'}
        </button>
        {note.trim() && !saving && (
          <button
            type="button"
            onClick={() => { setNote(''); setError(undefined); }}
            className="rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        )}
        {remaining <= 60 && (
          <span
            className={`ml-auto text-xs tabular-nums ${remaining === 0 ? 'text-destructive' : 'text-muted-foreground'}`}
          >
            {remaining} left
          </span>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-destructive" role="alert">{error}</p>}
    </section>
  );
}
